import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Signal,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, Subscription, map, skip, take } from 'rxjs';

import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces/gtm.interface';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { ConfigService, CmsDataService, sanitizeWhiteListedPath, stopPropagation } from '@fcom/core';
import { CmsPlaceholder, CmsTeaserCard } from '@fcom/core-api';
import { LoaderTheme } from '@fcom/ui-components/components/loader/enums';
import { LanguageService } from '@fcom/ui-translate';

import { ItemStatus, SearchHit } from './bluewings.interface';
import { BluewingsChipsService } from '../../../services/bluewings-chips/chips.service';

@Component({
  selector: 'fin-bluewings-search-api',
  templateUrl: './bluewings-search-api.component.html',
  styleUrl: './bluewings-search-api.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BluewingsSearchApiComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private configService = inject(ConfigService);
  private languageService = inject(LanguageService);
  private cmsDataService = inject(CmsDataService);
  private chipsService = inject(BluewingsChipsService);
  private gtmService = inject(GtmService);

  readonly LoaderTheme = LoaderTheme;
  readonly ItemStatus = ItemStatus;
  private subscription = new Subscription();

  content: InputSignal<CmsPlaceholder> = input<CmsPlaceholder>();

  chipsList: Signal<string[] | undefined> = computed(
    (): string[] | undefined => this.content().localSettings?.chips as string[] | undefined
  );
  limit: Signal<number> = computed((): number => (this.content().localSettings?.limit as number | undefined) ?? 6);
  locationCode: Signal<string> = computed((): string => {
    const contentId: string = this.content().id;
    const isLocation: boolean = contentId.includes('location');
    return isLocation ? contentId.split('location').pop().toLowerCase() : null;
  });
  path: Signal<string> = computed((): string => sanitizeWhiteListedPath(this.router.url, this.configService.cfg));
  parsedPageNum: Signal<number> = computed((): number => this.router.parseUrl(this.path()).queryParams.pageNum ?? 1);
  parsedOffset: Signal<number> = computed((): number =>
    this.parsedPageNum() ? (this.parsedPageNum() - 1) * this.limit() : null
  );
  strippedUrl: Signal<string> = computed((): string => this.path().replace(/[?&]pageNum=\d/, ''));
  urlDelimiter: Signal<string> = computed((): '?' | '&' => (this.strippedUrl().includes('?') ? '&' : '?'));

  currentPage$: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  items$: Observable<CmsTeaserCard[]>;
  itemsLoader$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  itemsLoadingStatus$: BehaviorSubject<ItemStatus> = new BehaviorSubject<ItemStatus>(ItemStatus.INITIAL);
  loadMoreItems$: BehaviorSubject<CmsTeaserCard[]> = new BehaviorSubject<CmsTeaserCard[]>([]);
  nextPageLink$: Observable<string>;
  numberOfPages$: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  offset$: BehaviorSubject<number> = new BehaviorSubject(null);
  showLoadMore$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  total$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  loadMoreLoading = false;

  ngOnInit(): void {
    this.offset$.next(this.parsedOffset());

    this.subscription.add(
      this.itemsLoadingStatus$.subscribe((state): void => {
        switch (state) {
          case ItemStatus.LOADING:
            this.itemsLoader$.next(true);
            break;
          case ItemStatus.DONE:
          case ItemStatus.INITIAL:
          default:
            this.itemsLoader$.next(false);
            break;
        }
      })
    );

    this.unfilteredSearch(this.parsedOffset());

    this.subscription.add(
      this.chipsService.filter$.pipe(skip(1)).subscribe((filter): void => {
        if (!filter) {
          this.unfilteredSearch(0);
          return;
        }
        this.itemsLoadingStatus$.next(ItemStatus.LOADING);
        const filterSearch$: Observable<SearchHit> = this.getListOfItems([filter]);

        this.items$ = filterSearch$.pipe(
          map((response): CmsTeaserCard[] => {
            if (response.numHits < 1) {
              this.itemsLoadingStatus$.next(ItemStatus.ERROR);
              return;
            }
            const pageCountWithoutOffset: number = Math.ceil(response.numHits / this.limit());

            this.itemsLoadingStatus$.next(ItemStatus.DONE);
            this.loadMoreItems$.next([]);
            this.total$.next(response.numHits);
            this.numberOfPages$.next(pageCountWithoutOffset);
            this.currentPage$.next(1);

            pageCountWithoutOffset > 1 ? this.showLoadMore$.next(true) : this.showLoadMore$.next(false);

            return response.articles;
          })
        );

        this.offset$.next(this.limit()); // reset offset for the pagination

        this.nextPageLink$ = this.currentPage$.pipe(map((page): string => this.pageCalc(page)));
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  pageCalc(page: number): string {
    return `${this.strippedUrl()}${this.urlDelimiter()}pageNum=${Number(page) + 1}`;
  }

  unfilteredSearch(offset: number): void {
    this.itemsLoadingStatus$.next(ItemStatus.LOADING);
    const filterSearch$: Observable<SearchHit> = this.getListOfItems(null, offset);

    this.items$ = filterSearch$.pipe(
      map((response): CmsTeaserCard[] => {
        if (response.numHits < 1) {
          this.itemsLoadingStatus$.next(ItemStatus.ERROR);
          return null;
        }
        const pageCountWithoutOffset: number = Math.ceil(response.numHits / this.limit());

        this.itemsLoadingStatus$.next(ItemStatus.DONE);
        this.loadMoreItems$.next([]);
        this.total$.next(response.numHits);
        this.numberOfPages$.next(pageCountWithoutOffset);
        this.currentPage$.next(this.parsedPageNum());

        pageCountWithoutOffset > 1 ? this.showLoadMore$.next(true) : this.showLoadMore$.next(false);

        return response.articles;
      })
    );

    this.offset$.next(offset + this.limit()); // reset offset for the pagination

    this.nextPageLink$ = this.currentPage$.pipe(map((page): string => this.pageCalc(page)));
  }

  getListOfItems(filter: string | string[] = null, offset = null): Observable<SearchHit> {
    return this.cmsDataService.getArticleListByTaxonomyTags({
      lang: this.languageService.langValue,
      locale: this.languageService.localeValue,
      taxonomyTags: this.content().subjectTaxonomyTags,
      filter: filter,
      location: this.locationCode(),
      limit: this.limit(),
      offset: offset,
    });
  }

  loadMore(event: Event): void {
    if (this.loadMoreLoading) {
      return;
    }
    stopPropagation(event);
    const nextPage: number = Number(this.currentPage$.value) + 1;
    const nextOffset: number = this.offset$.value + this.limit();
    const searchHits$: Observable<SearchHit> = this.getListOfItems(this.chipsService.filter$.value, this.offset$.value);

    this.loadMoreLoading = true;

    this.subscription.add(
      searchHits$
        .pipe(map((response): CmsTeaserCard[] => response.articles))
        .pipe(take(1))
        .subscribe((items): void => {
          const currentItems: CmsTeaserCard[] = this.loadMoreItems$.value ?? [];
          const validItems: CmsTeaserCard[] = items ?? [];
          this.loadMoreItems$.next([...currentItems, ...validItems]);
          this.loadMoreLoading = false;
        })
    );

    this.currentPage$.next(nextPage);

    if (this.numberOfPages$.value === this.currentPage$.value) {
      this.showLoadMore$.next(false);
    }
    this.offset$.next(nextOffset);
    this.trackElement('load-more');
  }

  private trackElement(label: string): void {
    this.gtmService.trackElement(label, GaContext.CONTENT, ElementTypes.BUTTON, undefined, ElementActions.CLICK);
  }
}
